import {
  AddIcCallSharp,
  CardGiftcardSharp,
  CheckCircleSharp,
  DashboardSharp,
  EmojiEventsSharp,
  ErrorSharp,
  ExpandLess,
  ExpandMore,
  FactCheckSharp,
  FolderCopySharp,
  GroupWorkSharp,
  HourglassBottomSharp,
  LibraryAddSharp,
  LocalOfferSharp,
  PeopleSharp,
  QrCode2Sharp,
  ReceiptLongSharp,
  SaveAsSharp,
  ScheduleSendSharp,
  ShoppingCartSharp,
  ViewListSharp,
  WifiCalling3Sharp,
  WorkspacePremiumSharp,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Avatar,
  Badge,
  Collapse,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { CSSObject, styled, Theme, useTheme } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import Toolbar from "@mui/material/Toolbar";
import type { JSX } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";

import { resolveDutiesFromBackendDuties } from "../../bolon/auth/resolveDutiesFromToken";
import AccessDenied from "../../components/AccessDenied";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import { useTrigger } from "../../contexts/TriggerContext";
import { viewImage } from "../../services/upload.service";
import {
  useAuthStore,
  useCurrentPageStore,
  usePortalStore,
} from "../../stores/store";
import { AuthenStoreType } from "../../types/authen";
import { InitialUserPageType, InitialUserPortalType } from "../../types/global";

const drawerWidth = 272;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

type MenuType = {
  text: string;
  icon?: JSX.Element;
  to?: string;
  open?: boolean;
  show?: boolean;
  children?: MenuType[];
};

export default function UserContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const isBolonRoute = useMatch("/bolon/*") !== null;
  const portal = usePortalStore(
    (state) => (state as InitialUserPortalType).portal,
  );
  const { user: auth, clearAuthState } = useAuthStore(
    (state) => state as AuthenStoreType,
  );
  const setCurrentPage = useCurrentPageStore(
    (state) => (state as InitialUserPageType).setCurrentPageState,
  );
  const { clearCurrentPageState } = useCurrentPageStore(
    (state) => state as InitialUserPageType,
  );

  const { countYourTask } = useTrigger();

  const [menus, setMenus] = useState<MenuType[]>([]);
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const TASK_CREATOR_MENU = useMemo(() => {
    return [
      {
        text: "Task Creation",
        icon: <LibraryAddSharp />,
        to: "/user/task-creator",
        show: auth?.duties?.includes("task"),
      },
      {
        text: "Key-In",
        icon: <SaveAsSharp />,
        open: false,
        children: [
          {
            text: "VIP",
            to: "/user/keyin/vip",
            show: auth?.duties?.includes("keyin"),
          },
          {
            text: "FIFO",
            to: "/user/keyin/fifo",
            show: auth?.duties?.includes("keyin"),
          },
          {
            text: "Stock",
            to: "/user/keyin/stock",
            show: auth?.duties?.includes("keyin"),
          },
          {
            text: "Key-In",
            to: "/user/keyin",
            show: auth?.duties?.includes("keyin"),
          },
        ],
        show: auth?.duties?.includes("keyin"),
      },
      {
        text: "Support",
        icon: <ScheduleSendSharp />,
        open: false,
        children: [
          {
            text: "Pending Ticket",
            to: "/user/support",
            show: auth?.duties?.includes("support"),
          },
          {
            text: "In Progress",
            to: "/user/support/in-progress",
            show: auth?.duties?.includes("support"),
          },
          {
            text: "Completed Ticket",
            to: "/user/support/completed",
            show: auth?.duties?.includes("support"),
          },
        ],
        show: auth?.duties?.includes("support"),
      },
      {
        text: "QC/Answer",
        icon: <FactCheckSharp />,
        open: false,
        children: [
          {
            text: "Call VIP",
            to: "/user/qc/call-jobs/vip",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "Call FIFO",
            to: "/user/qc/call-jobs/fifo",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "Call Stock",
            to: "/user/qc/call-jobs/stock",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "QC/Answer",
            to: "/user/qc",
            show: auth?.duties?.includes("qc"),
          },
        ],
        show: auth?.duties?.includes("qc"),
      },
      {
        text: "Task List",
        icon: <ViewListSharp />,
        to: "/user/tasks",
        show: true,
      },
      {
        text: "Your Task Creation",
        icon: (
          <Badge color="error" badgeContent={countYourTask?.task_creator || 0}>
            <ErrorSharp />
          </Badge>
        ),
        to: "/user/task-creator/your-tasks",
        show: true,
      },
    ];
  }, [countYourTask]);

  const KEYIN_MENU = useMemo(() => {
    return [
      {
        text: "Call Jobs",
        icon: <AddIcCallSharp />,
        open: false,
        children: [
          {
            text: "VIP",
            to: "/user/keyin/vip",
            show: auth?.duties?.includes("keyin"),
          },
          {
            text: "FIFO",
            to: "/user/keyin/fifo",
            show: auth?.duties?.includes("keyin"),
          },
          {
            text: "Stock",
            to: "/user/keyin/stock",
            show: auth?.duties?.includes("keyin"),
          },
        ],
        show: auth?.duties?.includes("keyin"),
      },
      {
        text: "Key-In",
        icon: <SaveAsSharp />,
        to: "/user/keyin",
        show: auth?.duties?.includes("keyin"),
      },
      {
        text: "Task Creation",
        icon: <LibraryAddSharp />,
        to: "/user/task-creator",
        show: auth?.duties?.includes("task"),
      },
      {
        text: "Support",
        icon: <ScheduleSendSharp />,
        open: false,
        children: [
          {
            text: "Pending Ticket",
            to: "/user/support",
            show: auth?.duties?.includes("support"),
          },
          {
            text: "In Progress",
            to: "/user/support/in-progress",
            show: auth?.duties?.includes("support"),
          },
          {
            text: "Completed Ticket",
            to: "/user/support/completed",
            show: auth?.duties?.includes("support"),
          },
        ],
        show: auth?.duties?.includes("support"),
      },
      {
        text: "QC/Answer",
        icon: <FactCheckSharp />,
        open: false,
        children: [
          {
            text: "Call VIP",
            to: "/user/qc/call-jobs/vip",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "Call FIFO",
            to: "/user/qc/call-jobs/fifo",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "Call Stock",
            to: "/user/qc/call-jobs/stock",
            show: auth?.duties?.includes("qc"),
          },
          {
            text: "QC/Answer",
            to: "/user/qc",
            show: auth?.duties?.includes("qc"),
          },
        ],
        show: auth?.duties?.includes("qc"),
      },
      {
        text: "Task List",
        icon: <ViewListSharp />,
        to: "/user/tasks",
        show: true,
      },
      {
        text: "Your Task Key-In",
        icon: (
          <Badge color="error" badgeContent={countYourTask?.key_in || 0}>
            <ErrorSharp />
          </Badge>
        ),
        to: "/user/keyin/your-tasks",
        show: true,
      },
    ];
  }, [countYourTask]);

  const SUPPORT_MENU = [
    {
      text: "Pending Ticket",
      icon: <ScheduleSendSharp />,
      to: "/user/support",
      show: auth?.duties?.includes("support"),
    },
    {
      text: "In Progress",
      icon: <HourglassBottomSharp />,
      to: "/user/support/in-progress",
      show: auth?.duties?.includes("support"),
    },
    {
      text: "Completed Ticket",
      icon: <CheckCircleSharp />,
      to: "/user/support/completed",
      show: auth?.duties?.includes("support"),
    },
    {
      text: "Task Creation",
      icon: <LibraryAddSharp />,
      to: "/user/task-creator",
      show: auth?.duties?.includes("task"),
    },
    {
      text: "Key-In",
      icon: <SaveAsSharp />,
      open: false,
      children: [
        {
          text: "VIP",
          to: "/user/keyin/vip",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "FIFO",
          to: "/user/keyin/fifo",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "Stock",
          to: "/user/keyin/stock",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "Key-In",
          to: "/user/keyin",
          show: auth?.duties?.includes("keyin"),
        },
      ],
      show: auth?.duties?.includes("keyin"),
    },
    {
      text: "QC/Answer",
      icon: <FactCheckSharp />,
      open: false,
      children: [
        {
          text: "Call VIP",
          to: "/user/qc/call-jobs/vip",
          show: auth?.duties?.includes("qc"),
        },
        {
          text: "Call FIFO",
          to: "/user/qc/call-jobs/fifo",
          show: auth?.duties?.includes("qc"),
        },
        {
          text: "Call Stock",
          to: "/user/qc/call-jobs/stock",
          show: auth?.duties?.includes("qc"),
        },
        {
          text: "QC/Answer",
          to: "/user/qc",
          show: auth?.duties?.includes("qc"),
        },
      ],
      show: auth?.duties?.includes("qc"),
    },
    {
      text: "Task List",
      icon: <ViewListSharp />,
      to: "/user/tasks",
      show: true,
    },
    // {
    //   text: "Your Task",
    //   icon: (
    //     <Badge color="error" overlap="circular" variant="dot">
    //       <ErrorSharp />
    //     </Badge>
    //   ),
    //   to: "",
    // },
  ];

  const QC_MENU = [
    {
      text: "Call Jobs",
      icon: <WifiCalling3Sharp />,
      open: false,
      children: [
        {
          text: "VIP",
          to: "/user/qc/call-jobs/vip",
          show: auth?.duties?.includes("qc"),
        },
        {
          text: "FIFO",
          to: "/user/qc/call-jobs/fifo",
          show: auth?.duties?.includes("qc"),
        },
        {
          text: "Stock",
          to: "/user/qc/call-jobs/stock",
          show: auth?.duties?.includes("qc"),
        },
      ],
      show: auth?.duties?.includes("qc"),
    },
    {
      text: "QC/Answer",
      icon: <FactCheckSharp />,
      to: "/user/qc",
      show: auth?.duties?.includes("qc"),
    },
    {
      text: "Task Creation",
      icon: <LibraryAddSharp />,
      to: "/user/task-creator",
      show: auth?.duties?.includes("task"),
    },
    {
      text: "Key-In",
      icon: <SaveAsSharp />,
      open: false,
      children: [
        {
          text: "VIP",
          to: "/user/keyin/vip",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "FIFO",
          to: "/user/keyin/fifo",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "Stock",
          to: "/user/keyin/stock",
          show: auth?.duties?.includes("keyin"),
        },
        {
          text: "Key-In",
          to: "/user/keyin",
          show: auth?.duties?.includes("keyin"),
        },
      ],
      show: auth?.duties?.includes("keyin"),
    },
    {
      text: "Support",
      icon: <ScheduleSendSharp />,
      open: false,
      children: [
        {
          text: "Pending Ticket",
          to: "/user/support",
          show: auth?.duties?.includes("support"),
        },
        {
          text: "In Progress",
          to: "/user/support/in-progress",
          show: auth?.duties?.includes("support"),
        },
        {
          text: "Completed Ticket",
          to: "/user/support/completed",
          show: auth?.duties?.includes("support"),
        },
      ],
      show: auth?.duties?.includes("support"),
    },
    {
      text: "Task List",
      icon: <ViewListSharp />,
      to: "/user/tasks",
      show: true,
    },
    // {
    //   text: "Your Task",
    //   icon: (
    //     <Badge color="error" overlap="circular" variant="dot">
    //       <ErrorSharp />
    //     </Badge>
    //   ),
    //   to: "/user/qc/your-tasks",
    // },
  ];

  const CRM_MENU: MenuType[] = [
    {
      text: "CRM",
      icon: <PeopleSharp />,
      to: "/crm",
      show: auth?.duties?.includes("crm"),
    },
  ];

  const REWARD_MENU: MenuType[] = [
    {
      text: "Reward",
      icon: <CardGiftcardSharp />,
      to: "/crm",
      show: auth?.duties?.includes("reward"),
    },
  ];

  // BOLON: the same navigation the standalone backoffice drew in its
  // BolonShellLayout, gated by the same resolver BolonLayout uses for its
  // RequireDuty gates (one backend duty on the login decides the internal
  // set). "Sales Group Tiers" (/bolon/tier-groups) is deliberately absent —
  // user decision 2026-09-04; the route still exists.
  const bolonInternalDuties = resolveDutiesFromBackendDuties(auth?.duties);
  const canBolon = (duty: string) => bolonInternalDuties.includes(duty);

  const BOLON_MENU: MenuType[] = [
    {
      text: "Dashboard",
      icon: <DashboardSharp />,
      to: "/bolon",
      show: canBolon("dashboard"),
    },
    {
      text: "Customers",
      icon: <PeopleSharp />,
      to: "/bolon/customers",
      show: canBolon("bolon"),
    },
    {
      text: "Customer Achievement",
      icon: <EmojiEventsSharp />,
      to: "/bolon/customer-achievements",
      show: canBolon("achievements"),
    },
    {
      text: "QR Management",
      icon: <QrCode2Sharp />,
      to: "/bolon/qr",
      show: canBolon("bolon"),
    },
    {
      text: "Orders",
      icon: <ShoppingCartSharp />,
      to: "/bolon/orders",
      show: canBolon("orders"),
    },
    {
      text: "Product Management",
      icon: <LocalOfferSharp />,
      to: "/bolon/catalog",
      show: canBolon("catalog"),
    },
    {
      text: "Reference Data",
      icon: <FolderCopySharp />,
      open: false,
      show: canBolon("catalog"),
      children: [
        {
          text: "Brands",
          to: "/bolon/catalog/brands",
          show: canBolon("catalog"),
        },
        {
          text: "Categories",
          to: "/bolon/catalog/categories",
          show: canBolon("catalog"),
        },
        {
          text: "Materials",
          to: "/bolon/catalog/materials",
          show: canBolon("catalog"),
        },
      ],
    },
    {
      text: "Claims",
      icon: <ReceiptLongSharp />,
      to: "/bolon/claims",
      show: canBolon("claims"),
    },
    {
      text: "Achievements",
      icon: <WorkspacePremiumSharp />,
      open: false,
      show: canBolon("tiers"),
      children: [
        {
          text: "Global Achievement",
          to: "/bolon/tiers",
          show: canBolon("tiers"),
        },
        {
          text: "One-Time Achievement",
          to: "/bolon/tiers/custom",
          show: canBolon("tiers"),
        },
      ],
    },
    {
      text: "User Groups",
      icon: <GroupWorkSharp />,
      to: "/bolon/user-groups",
      show: canBolon("user-groups"),
    },
  ];

  useEffect(() => {
    switch (portal) {
      case "TASK":
        setMenus(TASK_CREATOR_MENU);
        break;
      case "KEYIN":
        setMenus(KEYIN_MENU);
        break;
      case "SUPPORT":
        setMenus(SUPPORT_MENU);
        break;
      case "QC":
        setMenus(QC_MENU);
        break;
      case "CRM":
        setMenus(CRM_MENU);
        break;
      case "REWARD":
        setMenus(REWARD_MENU);
        break;
      case "BOLON":
        setMenus(BOLON_MENU);
        break;
      default:
        setMenus([]);
        break;
    }
    // return () => {
    //   setMenus([]);
    //   setOpen(false);
    //   setAnchorEl(null);
    //   setMobileOpen(false);
    //   setIsClosing(false);
    // };
  }, [portal, countYourTask]);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSignOut = () => {
    clearAuthState();
    clearCurrentPageState();
    navigate("/", { replace: true });
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      if (!isClosing) {
        if (mobileOpen) {
          const newMenus = menus.map((menu) => {
            if (menu?.open) {
              menu.open = false;
            }

            return { ...menu };
          });
          setMenus(newMenus);
        }
        setMobileOpen(!mobileOpen);
      }
    } else {
      if (open) {
        const newMenus = menus.map((menu) => {
          if (menu?.open) {
            menu.open = false;
          }

          return { ...menu };
        });
        setMenus(newMenus);
      }
      setOpen(!open);
    }
  };

  const handleClickMenu = (menu: MenuType) => {
    if (menu?.children && menu?.children?.length > 0) {
      if (mobileOpen || open) {
        const index = menus.findIndex((m) => m.text === menu.text);
        if (index !== -1) {
          const newMenus = [...menus];
          newMenus[index] = { ...newMenus[index], open: !newMenus[index].open };
          setMenus(newMenus);
        }
      } else {
        // if (isMobile) handleDrawerToggle();
        // navigate(menu.children[0].to);
        handleDrawerToggle();
      }
    } else {
      if (menu?.to) {
        if (isMobile) handleDrawerToggle();
        setCurrentPage(menu?.text ?? "");
        navigate(menu.to);
      }
    }
  };

  const drawer = (
    <List className="pt-[88px]">
      {menus.map((menu, index) => {
        if (menu?.show) {
          return (
            <ListItem
              key={`menu-${index}`}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                onClick={() => handleClickMenu(menu)}
                sx={[
                  {
                    minHeight: 48,
                    px: 2,
                  },
                  open
                    ? {
                        justifyContent: "initial",
                      }
                    : {
                        justifyContent: "center",
                      },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: "center",
                    },
                    isMobile
                      ? {
                          mr: 2,
                        }
                      : open
                        ? {
                            mr: 3,
                          }
                        : {
                            mr: "auto",
                          },
                  ]}
                >
                  {menu.icon}
                </ListItemIcon>
                <ListItemText
                  primary={menu.text}
                  // className="whitespace-break-spaces"
                  slotProps={{
                    primary: {
                      fontSize: theme.typography.body2.fontSize,
                    },
                  }}
                  sx={[
                    isMobile
                      ? {
                          display: "block",
                        }
                      : open
                        ? {
                            display: "block",
                          }
                        : {
                            display: "none",
                          },
                  ]}
                />
                {(mobileOpen || open) &&
                  menu?.children &&
                  menu?.children?.length > 0 && (
                    <>{menu?.open ? <ExpandLess /> : <ExpandMore />}</>
                  )}
              </ListItemButton>
              {menu?.children && menu?.children?.length > 0 && (
                <Collapse in={menu?.open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {menu.children.map((child, childIdx) => {
                      if (child?.show) {
                        return (
                          <ListItemButton
                            key={`child-menu-${childIdx}`}
                            onClick={() => handleClickMenu(child)}
                            sx={{ pl: 8 }}
                          >
                            {child?.icon && (
                              <ListItemIcon>{child.icon}</ListItemIcon>
                            )}
                            <ListItemText
                              primary={child?.text}
                              className="whitespace-break-spaces"
                              slotProps={{
                                primary: {
                                  fontSize: theme.typography.body2.fontSize,
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      }
                    })}
                  </List>
                </Collapse>
              )}
            </ListItem>
          );
        }
      })}
    </List>
  );

  return (
    <>
      <Box className="flex">
        <AppBar
          position="fixed"
          open={open}
          className="flex justify-center h-16"
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={[
                {
                  mr: 2,
                },
              ]}
            >
              <MenuIcon className="text-white" />
            </IconButton>
            <div className="w-[41px] h-[41px] flex-grow flex items-center font-bold">
              {/* <img
                src="/images/logo-essilor-luxottica.svg"
                className="w-[41px] h-[41px]"
                alt="essilor-logo"
              /> */}
              Essilor Luxottica
            </div>
            {auth && (
              <div>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar
                    src={auth?.image ? viewImage(auth?.image) : undefined}
                  />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 272,
                      },
                    },
                  }}
                >
                  <MenuItem>
                    <Stack
                      direction="row"
                      spacing={4}
                      useFlexGap
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={auth?.image ? viewImage(auth?.image) : undefined}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body1">{auth?.name}</Typography>
                        <Typography
                          variant="body2"
                          color="grey.400"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {auth?.role === "member" ? "User" : auth?.role}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => navigate("/user/profile-settings")}>
                    <Typography variant="body2">Profile Settings</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => navigate("/portal", { replace: true })}
                  >
                    <Typography variant="body2">Change Duty</Typography>
                  </MenuItem>

                  <MenuItem onClick={() => onSignOut()}>
                    <Typography variant="body2">Sign Out</Typography>
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
        </AppBar>
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiPaper-root": {
              paddingTop: "88px",
            },
          }}
        >
          {drawer}
        </MuiDrawer>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          {drawer}
        </Drawer>
        {/* The layout tweaks below apply to /bolon ONLY, so the existing pages
            keep their own layout exactly as on develop:
            - min-w-0: without it the flex item grows to the widest Bolon table
              instead of letting that table scroll inside it.
            - w-full instead of `container mx-auto`: the Tailwind container caps
              at 1200px, which left Bolon's wide tables with 88px side gutters.
            - no CustomBreadcrumbs: Bolon pages draw their own, and the empty
              <nav> would push their content 8px lower. */}
        <Box
          component="main"
          className={
            isBolonRoute ? "flex-grow min-w-0 py-6 px-8" : "flex-grow py-6 px-8"
          }
        >
          <DrawerHeader />
          <div className={isBolonRoute ? "w-full" : "container mx-auto"}>
            {isBolonRoute ? null : <CustomBreadcrumbs className="mb-2" />}
            {auth?.role !== "member" ? <AccessDenied /> : <Outlet />}
          </div>
        </Box>
      </Box>
    </>
  );
}
